import React, { useState, useEffect } from 'react';
import { useOrganization } from '@clerk/clerk-react';
import { useNavigate } from '@tanstack/react-router';
import { correlationId } from '@interlucent/ids';
import type { Stripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { send, waitForReady } from '../ws';
import { waitForAck } from '../utils/waitForAck';
import type { PlanTier } from '@interlucent/organization-protocol';
import { logger } from '../log';
import styles from './Onboarding.module.css';
import authStyles from './Auth.module.css';

const log = logger('onboarding');

// Lazy load Stripe only when needed
let stripePromise: Promise<Stripe | null> | null = null;
function getStripe() {
  if (!stripePromise) {
    // Dynamic import to avoid loading Stripe SDK on initial page load
    stripePromise = import('@stripe/stripe-js').then(({ loadStripe }) =>
      loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '')
    );
  }
  return stripePromise;
}

type PlansPayload = {
  plans: PlanTier[];
};

type SelectPlanResponse = {
  ok: boolean;
  progress: any;
  requiresPayment: boolean;
  clientSecret?: string;
  sessionId?: string;
};

type OnboardingStep = 'plan_selection' | 'checkout' | 'completed';

export const OnboardingFlow: React.FC = () => {
  const { organization } = useOrganization();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('plan_selection');
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [plans, setPlans] = useState<PlanTier[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  // Load plans on mount
  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      await waitForReady();
      const corr = correlationId();
      send('onboarding.getPlans', {}, corr);

      const data = await waitForAck<PlansPayload>(corr);

      setPlans(data.plans || []);
      // Auto-select free plan
      const freePlan = data.plans?.find((p: PlanTier) => p.tierSlug === 'free');
      if (freePlan) {
        setSelectedPlan(freePlan.id);
      }
    } catch (err) {
      log.error('failed to load plans', err);
      setError('Failed to load available plans. Please refresh the page.');
    } finally {
      setLoadingPlans(false);
    }
  };

  const handlePlanSelection = (planId: string) => {
    setSelectedPlan(planId);
    setError('');
  };

  const handlePlanSubmit = async () => {
    if (!selectedPlan) {
      setError('Please select a plan');
      return;
    }

    if (!organization) {
      setError('No organization found. Please refresh the page.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const plan = plans.find(p => p.id === selectedPlan);

      // Enterprise plan - show contact message
      if (plan?.tierSlug === 'enterprise') {
        setError('Please contact sales@interlucent.ai for Enterprise plans');
        setLoading(false);
        return;
      }

      await waitForReady();

      // Build return URL for Stripe embedded checkout
      const returnUrl = `${window.location.origin}/onboarding?session_id={CHECKOUT_SESSION_ID}`;

      // Send plan selection via WebSocket
      const corr = correlationId();
      send('onboarding.selectPlan', {
        planId: selectedPlan,
        orgId: organization.id,
        orgName: organization.name || 'Organization',
        returnUrl
      }, corr);

      const response = await waitForAck<SelectPlanResponse>(corr);

      if (response.requiresPayment && response.clientSecret) {
        // Paid plan - show embedded Stripe checkout
        setClientSecret(response.clientSecret);
        setCurrentStep('checkout');
      } else {
        // Free plan - onboarding complete, redirect to dashboard
        log.info('free plan selected, onboarding complete');
        navigate({ to: '/' });
      }
    } catch (err: any) {
      log.error('failed to select plan', err);
      setError(err.message || 'Failed to process plan selection');
    } finally {
      setLoading(false);
    }
  };

  // Handle return from Stripe checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
      // User has returned from Stripe checkout
      // The webhook should have updated the subscription status
      // Just redirect to dashboard
      log.info(`returned from Stripe checkout, session: ${sessionId}`);
      navigate({ to: '/' });
    }
  }, [navigate]);

  if (loadingPlans) {
    return (
      <div className={authStyles.container}>
        <div className={authStyles.card}>
          <div className={authStyles.header}>
            <h1 className={authStyles.title}>LOADING...</h1>
            <p className={authStyles.subtitle}>Fetching available plans</p>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Embedded Stripe Checkout
  if (currentStep === 'checkout' && clientSecret) {
    return (
      <div className={authStyles.container}>
        <div className={authStyles.card} style={{ maxWidth: '600px' }}>
          <div className={authStyles.header}>
            <h1 className={authStyles.title}>COMPLETE PAYMENT</h1>
            <p className={authStyles.subtitle}>
              Enter your payment details to activate your plan
            </p>
          </div>

          <div className={styles.checkoutContainer}>
            <EmbeddedCheckoutProvider
              stripe={getStripe()}
              options={{ clientSecret }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>

          <button
            onClick={() => {
              setCurrentStep('plan_selection');
              setClientSecret(null);
            }}
            className={authStyles.linkButton}
            style={{ marginTop: '16px' }}
          >
            ← Back to plan selection
          </button>
        </div>
      </div>
    );
  }

  // Step 1: Plan Selection
  return (
    <div className={authStyles.container}>
      <div className={authStyles.card}>
        <div className={authStyles.header}>
          <h1 className={authStyles.title}>CHOOSE YOUR PLAN</h1>
          <p className={authStyles.subtitle}>
            Select a plan for <strong>{organization?.name || 'your organization'}</strong>
          </p>
        </div>

        {error && <div className={authStyles.error}>{error}</div>}

        <div className={styles.plansList}>
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`${styles.planCard} ${selectedPlan === plan.id ? styles.selectedPlan : ''}`}
              onClick={() => handlePlanSelection(plan.id)}
            >
              <div className={styles.planHeader}>
                <h3 className={styles.planName}>{plan.tierName}</h3>
                <div className={styles.planPrice}>
                  {plan.priceMonthlyCents === null
                    ? (plan.tierSlug === 'enterprise' ? 'Contact Us' : 'FREE')
                    : `$${(plan.priceMonthlyCents / 100).toFixed(0)}/mo`
                  }
                </div>
              </div>
              <ul className={styles.planFeatures}>
                <li>{plan.quotaMaxProjects} projects</li>
                <li>{plan.quotaMaxAppsPerProject} apps per project</li>
                <li>{plan.quotaMaxVersionsPerApp} versions per app</li>
                <li>{plan.quotaMaxConcurrentStreams} concurrent streams</li>
              </ul>
              <div className={styles.planSelect}>
                {selectedPlan === plan.id && <span>✓ Selected</span>}
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={handlePlanSubmit}
          disabled={loading || !selectedPlan}
          className={authStyles.button}
        >
          {loading ? 'PROCESSING...' : 'CONTINUE'}
        </button>
      </div>
    </div>
  );
};
